'use client';

import { type FC, type HTMLAttributes, useState } from 'react';
import { isIOS } from 'react-device-detect';
import { buttonVariants } from '@paypoteam/ui-components';
import { Star, X } from '@phosphor-icons/react';
import Link from 'next/link';

import { cn } from '@/lib/utils';

import { LocalImage } from '@/components/atoms/LocalImage';

import { type MobileAppBarType } from './MobileAppBar.types';

export interface MobileAppBarProps extends HTMLAttributes<HTMLAnchorElement> {
    config: MobileAppBarType;
}

const MobileAppBar: FC<MobileAppBarProps> = ({ config }) => {
    const {
        header,
        image,
        rating,
        text,
        buttonText,
        platformLinks: { iosLink, androidLink },
    } = config;

    const [isMobileAppBarVisible, setMobileAppBarVisible] = useState(true);

    const storeLink = isIOS ? iosLink : androidLink;

    return (
        <div className="z-10 flex w-full items-center justify-center bg-grey-100 px-6 dark:bg-grey-800 md:hidden">
            <div
                className={cn(
                    'z-[60] mx-auto my-0 flex w-full max-w-[1168px] items-center justify-center gap-3 py-2 md:hidden',
                    !isMobileAppBarVisible && 'hidden',
                )}
            >
                <LocalImage
                    {...image}
                    width={45}
                    height={48}
                />

                <div className="flex flex-1 flex-col items-start font-semibold">
                    <p className="text-3xs font-bold sm:text-2xs">{header}</p>

                    <div className="flex w-full items-center gap-1 text-3xs">
                        <p>{rating}</p>

                        <div className="flex">
                            <Star
                                size={7}
                                weight="fill"
                                className="fill-typo-primary"
                            />

                            <Star
                                size={7}
                                weight="fill"
                                className="fill-typo-primary"
                            />

                            <Star
                                size={7}
                                weight="fill"
                                className="fill-typo-primary"
                            />

                            <Star
                                size={7}
                                weight="fill"
                                className="fill-typo-primary"
                            />

                            <Star
                                size={7}
                                weight="fill"
                                className="fill-typo-primary"
                            />
                        </div>
                    </div>

                    <p className="text-4xs">{text}</p>
                </div>

                <Link
                    href={storeLink}
                    className={buttonVariants({
                        variant: 'secondary',
                        size: 's',
                    })}
                >
                    {buttonText}
                </Link>

                <button
                    className="h-6 w-6 bg-transparent p-0"
                    onClick={() => setMobileAppBarVisible(false)}
                >
                    <X
                        size={24}
                        weight="bold"
                    />
                </button>
            </div>
        </div>
    );
};

export default MobileAppBar;
